import {
  PublicClientApplication,
  AuthenticationResult,
  Configuration,
  LogLevel,
  AccountInfo,
  PopupRequest,
  EndSessionRequest,
  SsoSilentRequest
} from '@azure/msal-browser';

/**
 * Configuration class for @azure/msal-browser:
 * https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/modules/_src_config_configuration_.html
 */
const MSAL_CONFIG: Configuration = {
  auth: {
    clientId: process.env.VUE_APP_AZURE_CLIENT_ID,
    authority: process.env.VUE_APP_AZURE_AUTHORITY,
    redirectUri: process.env.VUE_APP_AZURE_REDIRECT_URI
  },
  cache: {
    cacheLocation: 'sessionStorage', // This configures where your cache will be stored
    storeAuthStateInCookie: false // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      }
    }
  }
};

/**
 * AuthModule for application - handles authentication in app.
 */
class AuthModule {
  private myMSALObj: PublicClientApplication; // https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/classes/_src_app_publicclientapplication_.publicclientapplication.html
  private account: AccountInfo | null; // https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-common/modules/_src_account_accountinfo_.html
  private loginRequest: PopupRequest; // https://azuread.github.io/microsoft-authentication-library-for-js/ref/msal-browser/modules/_src_request_popuprequest_.html
  private silentLoginRequest: SsoSilentRequest;

  constructor() {
    this.myMSALObj = new PublicClientApplication(MSAL_CONFIG);
    this.account = null;

    this.loginRequest = {
      prompt: 'select_account',
      scopes: ['openid', 'profile', 'User.Read', 'email']
    };

    this.silentLoginRequest = {};
  }

  /**
   * Calls getAllAccounts and determines the correct account to sign into, currently defaults to first account found in cache.
   * TODO: Add account chooser code
   *
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
   */
  private getAccount(): AccountInfo | null {
    const currentAccounts = this.myMSALObj.getAllAccounts();

    if (currentAccounts === null) {
      console.log('No accounts detected');
      return null;
    }

    if (currentAccounts.length > 1) {
      // Add choose account code here
      console.log(
        'Multiple accounts detected, need to add choose account code.'
      );
      return currentAccounts[0];
    } else if (currentAccounts.length === 1) {
      return currentAccounts[0];
    }

    return null;
  }

  /**
   * Handles the response from a popup or redirect. If response is null, will check if we have any accounts and attempt to sign in.
   * @param response
   */
  handleResponse(response: AuthenticationResult | null) {
    if (response !== null) {
      this.account = response.account;
    } else {
      this.account = this.getAccount();
    }

    return this.account;
  }

  /**
   * Calls ssoSilent in order to retrieve the ID token, which is not currently
   * exposed on the Authenticat
   * @param request
   */
  async retrieveIdToken(account: AccountInfo) {
    if (!this.account) {
      return null;
    }

    this.silentLoginRequest.account = account;

    return this.myMSALObj.ssoSilent(this.silentLoginRequest).catch(error => {
      console.error('Silent Error: ' + error);

      throw error;
    });
  }

  /**
   * Calls loginPopup or loginRedirect based on given signInType.
   * @param signInType
   */
  async login(): Promise<string> {
    return this.myMSALObj
      .loginPopup(this.loginRequest)
      .then((result: AuthenticationResult) => {
        const idToken = result.idToken;

        return idToken;
      })
      .catch(err => {
        console.error(err);
        throw err;
      });
  }

  /**
   * Logs out of current account.
   */
  logout(): void {
    if (!this.account) {
      return;
    }

    const logOutRequest: EndSessionRequest = {
      account: this.account
    };

    this.myMSALObj.logoutRedirect(logOutRequest);
  }

  isSignedIn(): boolean {
    return !!this.account;
  }
}

export default new AuthModule();
